import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Search, MailCheck, MailX, MailWarning, RotateCw, Check } from "lucide-react";
import { api, formatErr, useAuth } from "../lib/auth";
import { AdminShell } from "../components/AdminShell";

const STATUSES = [
  ["new", "Nouveau"],
  ["contacted", "Contacté"],
  ["in_progress", "En cours"],
  ["closed", "Clôturé"],
];

const EMAIL_STATE = {
  sent: { Icon: MailCheck, cls: "text-emerald-400", label: "Confirmation envoyée" },
  skipped: { Icon: MailWarning, cls: "text-amber-400", label: "Non envoyée (limite anti-abus)" },
  failed: { Icon: MailX, cls: "text-red-400", label: "Échec d'envoi" },
  pending: { Icon: MailWarning, cls: "text-stone", label: "En attente" },
};

const EmailBadge = ({ c, onResend, busy }) => {
  const p = c.emails?.prospect;
  const state = EMAIL_STATE[p?.status] || EMAIL_STATE.pending;
  const { Icon } = state;
  return (
    <div className="space-y-2" data-testid={`lead-email-${c.id}`}>
      <p className={`inline-flex items-center gap-1.5 text-xs ${state.cls}`} data-testid={`lead-email-status-${c.id}`} title={p?.at ? new Date(p.at).toLocaleString("fr-FR") : ""}>
        <Icon size={14} /> {state.label}
      </p>
      {c.emails?.owner?.status === "failed" && <p className="text-[11px] text-red-400">Alerte interne non envoyée</p>}
      {p?.status !== "sent" && (
        <button data-testid={`lead-resend-${c.id}`} disabled={busy} onClick={() => onResend(c.id)} className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-[var(--bone)] disabled:opacity-40 transition-colors duration-300">
          <RotateCw size={12} className={busy ? "animate-spin" : ""} /> Renvoyer
        </button>
      )}
    </div>
  );
};

const Row = ({ c, onStatus, onDelete, onResend, busy }) => (
  <tr className="border-b border-line align-top" data-testid={`lead-row-${c.id}`}>
    <td className="py-5 pr-4 font-mono text-xs text-stone whitespace-nowrap">{new Date(c.created_at).toLocaleString("fr-FR")}</td>
    <td className="py-5 pr-4">
      <p className="font-medium">{c.name}</p>
      <a href={`mailto:${c.email}`} className="text-sm text-gold">{c.email}</a>
      {c.company && <p className="text-sm text-stone">{c.company}</p>}
    </td>
    <td className="py-5 pr-4 text-sm text-stone">
      <p>{c.service || "—"}</p><p>{c.budget || "—"}</p><p>{c.city || "—"} · {String(c.lang || "").toUpperCase()}</p>
    </td>
    <td className="py-5 pr-4 text-sm max-w-md whitespace-pre-line">{c.message}</td>
    <td className="py-5 pr-4"><EmailBadge c={c} onResend={onResend} busy={busy} /></td>
    <td className="py-5 pr-4">
      <select data-testid={`lead-status-${c.id}`} value={c.status} onChange={(e) => onStatus(c.id, e.target.value)} className="bg-[var(--ink-3)] border border-line px-3 py-2 text-sm">
        {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {c.status === "new" && (
        <button data-testid={`lead-mark-contacted-${c.id}`} onClick={() => onStatus(c.id, "contacted")} className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-[var(--bone)] transition-colors duration-300">
          <Check size={12} /> Marquer contacté
        </button>
      )}
    </td>
    <td className="py-5">
      <button data-testid={`lead-delete-${c.id}`} onClick={() => onDelete(c.id)} className="p-2 text-stone hover:text-red-400 transition-colors duration-300" aria-label="delete"><Trash2 size={18} /></button>
    </td>
  </tr>
);

export default function Admin() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/contacts").then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, [user]);

  const shown = useMemo(() => leads.filter((l) =>
    (filter === "all" || l.status === filter) &&
    `${l.name} ${l.email} ${l.company} ${l.message}`.toLowerCase().includes(q.toLowerCase())
  ), [leads, q, filter]);

  if (user === null) return <div className="min-h-screen grid place-items-center text-stone">…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const replace = (data) => setLeads((ls) => ls.map((l) => (l.id === data.id ? data : l)));
  const onStatus = async (id, status) => {
    const { data } = await api.patch(`/contacts/${id}`, { status });
    replace(data);
    toast.success(status === "contacted" ? "Marqué comme contacté" : "Statut mis à jour");
  };
  const onResend = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/contacts/${id}/resend-confirmation`);
      replace(data);
      toast.success("Confirmation renvoyée au prospect");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Envoi impossible");
    } finally {
      setBusyId(null);
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/contacts/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  return (
    <AdminShell active="leads">
      <h1 className="font-display text-5xl mt-16">Demandes <em className="text-gold">entrantes</em></h1>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["all", "Toutes"], ...STATUSES].map(([v, l]) => (
          <button key={v} data-testid={`admin-filter-${v}`} onClick={() => setFilter(v)} className={`border px-4 py-4 text-left transition-colors duration-300 ${filter === v ? "border-[var(--gold)]" : "border-line hover:border-stone"}`}>
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone">{l}</p>
            <p className="font-display text-3xl mt-1" data-testid={`admin-count-${v}`}>{v === "all" ? leads.length : leads.filter((x) => x.status === v).length}</p>
          </button>
        ))}
      </div>
      <div className="mt-8 flex items-center gap-3 border-b border-line max-w-md">
        <Search size={16} className="text-stone" />
        <input data-testid="admin-search-input" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 bg-transparent py-3 focus:outline-none" />
      </div>
      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[1100px] text-left" data-testid="admin-leads-table">
          <thead className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone border-b border-line">
            <tr><th className="py-3">Date</th><th>Contact</th><th>Projet</th><th>Message</th><th>Email prospect</th><th>Statut</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((c) => <Row key={c.id} c={c} onStatus={onStatus} onDelete={onDelete} onResend={onResend} busy={busyId === c.id} />)}
          </tbody>
        </table>
        {shown.length === 0 && <p className="py-16 text-center text-stone" data-testid="admin-empty">Aucune demande pour le moment.</p>}
      </div>
    </AdminShell>
  );
}
